import React from 'react';
import clsx from 'clsx';

interface ResumoFacetaSecaoProps<K extends string> {
  titulo: string;
  /** Ordem de exibição — a mesma do Record de rótulos. */
  opcoes: K[];
  rotulos: Record<K, string>;
  contagem: Map<K, number>;
  selecionados: K[];
  onChange: (selecionados: K[]) => void;
  /** Bolinha colorida antes do rótulo (ex.: cor do nível de urgência). */
  classePonto?: Record<K, string>;
}

function alternar<T>(lista: T[], valor: T): T[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

/**
 * Uma linha do painel de resumo — título à esquerda, opções lado a lado ("rótulo número"),
 * cada uma clicável (liga/desliga, várias ao mesmo tempo). Opções zeradas ficam de fora
 * (só poluiriam), a não ser que estejam selecionadas — senão não daria pra desmarcar.
 */
export default function ResumoFacetaSecao<K extends string>({
  titulo,
  opcoes,
  rotulos,
  contagem,
  selecionados,
  onChange,
  classePonto,
}: ResumoFacetaSecaoProps<K>) {
  const visiveis = opcoes.filter((opcao) => (contagem.get(opcao) ?? 0) > 0 || selecionados.includes(opcao));

  return (
    <div className="flex items-start gap-3">
      <p className="w-28 shrink-0 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{titulo}</p>
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        {visiveis.length === 0 && <span className="py-0.5 text-sm text-gray-400">—</span>}
        {visiveis.map((opcao) => {
          const selecionado = selecionados.includes(opcao);
          return (
            <button
              key={opcao}
              type="button"
              onClick={() => onChange(alternar(selecionados, opcao))}
              aria-pressed={selecionado}
              className={clsx(
                'flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-sm transition',
                selecionado
                  ? 'border-blue-600 bg-blue-50 font-medium text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              )}
            >
              {classePonto && <span className={clsx('h-2 w-2 shrink-0 rounded-full', classePonto[opcao])} />}
              {rotulos[opcao]}
              <span className={clsx('tabular-nums', selecionado ? 'text-blue-700' : 'text-gray-400')}>
                {contagem.get(opcao) ?? 0}
              </span>
            </button>
          );
        })}
        {selecionados.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="px-1 text-xs text-blue-600 hover:underline">
            Limpar
          </button>
        )}
      </div>
    </div>
  );
}
